import React, { useRef } from "react";

const vesselStageAspectRatios = {
  "12oz Can": "105 / 314",
  "16oz Can": "1377 / 3950",
  "22oz Bomber": "155 / 599",
  "750ml": "165 / 613",
  "750ml - Green": "165 / 613"
};

const defaultBottleAspectRatio = "151 / 599";


function CellarBeerView({ beer, quantity, can12Height = 200 }) {
  const showingPhoto = true;

  // For measuring container size (somewhat like GeometryReader):
  const containerRef = useRef(null);
  // useLayoutEffect(() => {
  //   if (!containerRef.current) return;

  //   // Basic approach: measure once on mount
  //   const { width, height } = containerRef.current.getBoundingClientRect();
  //   setBottleWidth(width);
  //   setBottleHeight(height);

  //   // If you need dynamic resizing, you’d attach a ResizeObserver
  // }, []);

  const beerImageURL = beer.craftBeerEntry.beerImage || null;
  const beerBottleSize = beer.craftBeerEntry.beerBottleSize;
  const vesselStageAspectRatio =
    vesselStageAspectRatios[beerBottleSize] || defaultBottleAspectRatio;

  // A small utility to show the "label" or user photo
  // with certain dimensions. We’ll just do a quick example:
  function renderLabelImage({
    topPercent = 20,
    leftPercent = 21,
    widthPercent = 58,
    heightPercent = 70
  }) {
    if (!showingPhoto || !beerImageURL) return null;
    return (
      <img
        src={beerImageURL}
        alt="User Beer Label"
        style={{
          position: "absolute",
          top: `${topPercent}%`,
          left: `${leftPercent}%`,
          width: `${widthPercent}%`,
          height: `${heightPercent}%`,
          objectFit: "cover"
        }}
      />
    );
  }

  // Swift does if/else checks on beerBottleSize, layering images inside.
  // We can do a switch statement in React:
  let content;
  switch (beerBottleSize) {
    case "12oz Can":
      content = (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "70%",
          }}
        >
          {/* Diffuse */}
          <img
            src={"/images/Can12Diffuse.png"}
            alt="Can12Diffuse"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain"
            }}
          />

          {/* Reflection */}
          <img
            src={"/images/Can12Reflection.png"}
            alt="Can12Reflection"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />

          {/* Another reflection or specular */}
          <img
            src={"/images/Can12Specular.png"}
            alt="Can12Specular"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.75
            }}
          />

          {/* Extended texture */}
          <img
            src={"/images/Can12ExTex.png"}
            alt="Can12ExTex"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply",
              opacity: 0.4
            }}
          />

          {/* The user’s label image, approximating Swift’s .scaledToFill .clipShape */}
          {renderLabelImage({})}

          {/* Shadows */}
          <img
            src={"/images/Can12Shadows.png"}
            alt="Can12Shadows"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply",
              opacity: 0.5
            }}
          />

          {/* If quantity > 1 => text at bottom */}
          {quantity > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: "5px",
                width: "100%",
                textAlign: "center",
                color: "white",
                textShadow: "0 0 5px black"
              }}
            >
              {quantity}
            </div>
          )}
        </div>
      );
      break;

    case "16oz Can":
      content = (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "90%",
          }}
        >
          {/* Base / Diffuse */}
          <img
            src={"/images/CanDiffuse.png"}
            alt="CanDiffuse"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain"
            }}
          />

          {/* Reflection (possibly duplicated, if you want the Swift effect) */}
          <img
            src={"/images/CanReflection.png"}
            alt="CanReflection"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />
          <img
            src={"/images/CanReflection.png"}
            alt="CanReflection2"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />

          {/* Specular highlight */}
          <img
            src={"/images/CanSpecular.png"}
            alt="CanSpecular"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              // opacity: 0.75, // if you want partial transparency
            }}
          />

          {/* Extended texture */}
          <img
            src={"/images/CanExTex.png"}
            alt="CanExTex"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply",
              opacity: 0.5
            }}
          />

          {/* If you have a label image, drop it in (like Swift’s label area) */}
          {renderLabelImage({
            // For example, fill ~98% width and 75% height:
            topPercent: 20,
            leftPercent: 40,
            widthPercent: 20,
            heightPercent: 65
          })}

          {/* Shadows */}
          <img
            src={"/images/Can16Shadows.png"}
            alt="Can16Shadows"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply",
              opacity: 0.75
            }}
          />

          {/* Quantity text at bottom if > 1 */}
          {quantity > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: "5px",
                width: "100%",
                textAlign: "center",
                color: "white",
                textShadow: "0 0 5px black"
              }}
            >
              {quantity}
            </div>
          )}
        </div>
      );
      break;

    case "22oz Bomber":
      content = (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%"
          }}
        >
          {/* Base GI */}
          <img
            src="/images/BomberGI.png"
            alt="BomberGI"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain"
            }}
          />

          {/* Reflection */}
          <img
            src="/images/BomberReflection.png"
            alt="BomberReflection"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.75
            }}
          />

          {/* Specular */}
          <img
            src="/images/BomberSpecularLabel.png"
            alt="BomberSpecular"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.75
            }}
          />

          {/* Refraction (twice) */}
          <img
            src="/images/BomberRefraction.png"
            alt="BomberRefraction1"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.5
            }}
          />
          <img
            src="/images/BomberRefraction.png"
            alt="BomberRefraction2"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.5
            }}
          />

          {/* User’s label image around the lower portion of the bottle */}
          {renderLabelImage({
            topPercent: 45,  /* or 66%? Adjust to match your art */
            leftPercent: 28,
            widthPercent: 44,
            heightPercent: 45
          })}

          {/* BomberSpecularLabel mask */}
          <img
            src="/images/BomberSpecularLabel.png"
            alt="BomberSpecularLabel"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />

          {/* Shadows */}
          {/* <img
            src="/images/BomberShadows.png"
            alt="BomberShadows"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply",
              opacity: 0.25
            }}
          /> */}

          {/* Extended texture */}
          <img
            src="/images/BomberExTex.png"
            alt="BomberExTex"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply"
            }}
          />

          {/* If quantity > 1 => text at bottom */}
          {quantity > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: "5px",
                width: "100%",
                textAlign: "center",
                color: "white",
                textShadow: "0 0 5px black"
              }}
            >
              {quantity}
            </div>
          )}
        </div>
      );
      break;

    case "12oz Stubby Lager":
      content = (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%"
          }}
        >
          {/* Base GI */}
          <img
            src="/images/StubbyLagGI.png"
            alt="StubbyLagGI"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain"
            }}
          />

          {/* Reflection */}
          <img
            src="/images/StubbyLagReflection.png"
            alt="StubbyLagReflection"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.25
            }}
          />

          {/* Refraction */}
          <img
            src="/images/StubbyLagRefraction.png"
            alt="StubbyLagRefraction"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />

          {/* Specular */}
          <img
            src="/images/StubbyLagSpecularLabel.png"
            alt="StubbyLagSpecular"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.75
            }}
          />

          {/* Label area: (like Swift's Spacer + a container for the user photo) */}
          {/* If you want the label in the lower half, adjust top/left/width/height */}
          {renderLabelImage({
            // Example: fill 50% of the container’s height, 0.5 from the top
            topPercent: 50,
            leftPercent: 0,
            widthPercent: 100,
            heightPercent: 50
          })}

          {/* Specular label mask (like Swift's StubbyLagSpecularLabel) */}
          <img
            src="/images/StubbyLagSpecularLabel.png"
            alt="StubbyLagSpecularLabel"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />

          {/* Shadows */}
          <img
            src="/images/StubbyLagShadows.png"
            alt="StubbyLagShadows"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply",
              opacity: 0.25
            }}
          />

          {/* Extended texture */}
          <img
            src="/images/StubbyLagExTex.png"
            alt="StubbyLagExTex"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply"
            }}
          />

          {/* If quantity > 1 => show text near bottom */}
          {quantity > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: "5px",
                width: "100%",
                textAlign: "center",
                color: "white",
                textShadow: "0 0 5px black"
              }}
            >
              {quantity}
            </div>
          )}
        </div>
      );
      break;

    case "750ml":
      content = (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%"
          }}
        >
          {/* Base GI */}
          <img
            src="/images/750GI.png"
            alt="750GI"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain"
            }}
          />

          {/* Refraction */}
          <img
            src="/images/750Refraction.png"
            alt="750Refraction"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />

          {/* User’s label image (approx halfway down the bottle) */}
          {renderLabelImage({
            topPercent: 55,
            leftPercent: 27,
            widthPercent: 46,
            heightPercent: 35
          })}

          {/* Specular label mask */}
          <img
            src="/images/750SpecularLabel.png"
            alt="750SpecularLabel"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.2
            }}
          />

          {/* Reflection */}
          <img
            src="/images/750Reflection.png"
            alt="750Reflection"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.55
            }}
          />

          {/* Specular */}
          <img
            src="/images/750Specular.png"
            alt="750Specular"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.95
            }}
          />

          {/* Shadows */}
          {/* <img
        src="/images/750Shadows.png"
        alt="750Shadows"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          mixBlendMode: "multiply",
          opacity: 0.5
        }}
      /> */}

          {/* Extended texture */}
          <img
            src="/images/750ExTex.png"
            alt="750ExTex"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply"
            }}
          />

          {/* If quantity > 1 => text at bottom */}
          {quantity > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: "5px",
                width: "100%",
                textAlign: "center",
                color: "white",
                textShadow: "0 0 5px black"
              }}
            >
              {quantity}
            </div>
          )}
        </div>
      );
      break;

    case "750ml - Green":
      content = (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%"
          }}
        >
          {/* Base GI */}
          <img
            src="/images/750GI.png"
            alt="750GI"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain"
            }}
          />

          {/* RefractionGreen (used twice in Swift) */}
          <img
            src="/images/750RefractionGreen.png"
            alt="750RefractionGreen1"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />
          <img
            src="/images/750RefractionGreen.png"
            alt="750RefractionGreen2"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />

          {/* The user’s label image, around halfway down the bottle */}
          {renderLabelImage({
            topPercent: 55,
            leftPercent: 26.5,
            widthPercent: 47.5,
            heightPercent: 35
          })}

          {/* Specular label mask */}
          <img
            src="/images/750SpecularLabel.png"
            alt="750SpecularLabel"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.8
            }}
          />

          {/* 750 Reflection */}
          <img
            src="/images/750Reflection.png"
            alt="750Reflection"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.75
            }}
          />

          {/* 750 Specular */}
          <img
            src="/images/750Specular.png"
            alt="750Specular"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.75
            }}
          />

          {/* Shadows */}
          {/* <img
              src="/images/750Shadows.png"
              alt="750Shadows"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                mixBlendMode: "multiply",
                opacity: 0.5
              }}
            /> */}

          {/* Extended texture */}
          <img
            src="/images/750ExTex.png"
            alt="750ExTex"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply"
            }}
          />

          {/* If quantity > 1 => show text at bottom */}
          {quantity > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: "5px",
                width: "100%",
                textAlign: "center",
                color: "white",
                textShadow: "0 0 5px black"
              }}
            >
              {quantity}
            </div>
          )}
        </div>
      );
      break;

    default:
      content = (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          {/* Base GI */}
          <img
            src="/images/BottleGI.png"
            alt="BottleGI"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain"
            }}
          />

          {/* Refraction (check if it's "12oz - Green") */}
          {beerBottleSize === "12oz - Green" ? (
            <img
              src="/images/BottleRefractionGreen.png"
              alt="BottleRefractionGreen"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                mixBlendMode: "screen"
              }}
            />
          ) : (
            <img
              src="/images/BottleRefraction.png"
              alt="BottleRefraction"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                mixBlendMode: "screen"
              }}
            />
          )}

          {/* Specular */}
          <img
            src="/images/BottleSpecular.png"
            alt="BottleSpecular"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen"
            }}
          />

          {/* The user’s label image (like Swift’s .scaledToFill .clipShape) */}
          {/* For example, we place it around half-way down the bottle */}
          {renderLabelImage({
            topPercent: 50,
            leftPercent: 28,
            widthPercent: 44,
            heightPercent: 40
          })}

          {/* Optional mask for the label specular, like Swift’s BottleSpecularLabel */}
          {/* If you want that layered effect, you can do something like: */}
          <img
            src="/images/BottleSpecularLabel.png"
            alt="BottleSpecularLabel"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "screen",
              opacity: 0.8
            }}
          />

          {/* Shadows */}
          <img
            src="/images/BottleShadows.png"
            alt="BottleShadows"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply",
              opacity: 0.5
            }}
          />

          {/* Extended texture */}
          <img
            src="/images/BottleExTex.png"
            alt="BottleExTex"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              mixBlendMode: "multiply",
              opacity: 0.4
            }}
          />

          {/* If quantity > 1 => text at bottom */}
          {quantity > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: "5px",
                width: "100%",
                textAlign: "center",
                color: "white",
                textShadow: "0 0 5px black"
              }}
            >
              {quantity}
            </div>
          )}
        </div>
      );
      break;
  }

  return (
    <div
      ref={containerRef}
      className="cellar-beer-view"
    >
      <div
        className="cellar-beer-stage"
        style={{ aspectRatio: vesselStageAspectRatio }}
      >
        {content}
      </div>
    </div>
  );
}

export default CellarBeerView;
